import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import type { DeploymentTopologyEnvironmentType } from '@wix/cli-astro-definitions';
import { useDevCenterOverridesClient } from '@wix/dev-center-overrides-client';
import { isInsideGitRepo, getGitLatestCommitHash } from '@wix/package-manager';
import { useProjectModel } from '../../model.js';
import { assertBuildOutputExists } from '../../files/assert-build-output-exists.js';
import { ActionStatus } from './ActionStatus.js';
import { useLoadBuildFiles } from './hooks/useLoadBuildFiles.js';
import { useUpdateManifest } from './hooks/useUpdateManifest.js';
import { useDeployFiles } from './hooks/useDeployFiles.js';
import { readAppManifest } from '../../app-manifest/file.js';
import { SiteTopologyAndLinks } from './SiteTopologyAndLinks.js';
import { AppTopologyAndLinks } from './AppTopologyAndLinks.js';

export const Preview: FC<{
  environment: DeploymentTopologyEnvironmentType;
  baseUrl?: string;
  label?: string;
  onPreviewCreated?: (args: { previewId: string; previewUrl?: string }) => void;
}> = ({ environment, baseUrl, label, onPreviewCreated }) => {
  const { model } = useProjectModel();

  const devCenterOverrides = useDevCenterOverridesClient();
  const loadBuildFiles = useLoadBuildFiles();
  const deployFiles = useDeployFiles();
  const { updateManifestWithBackendWorker } = useUpdateManifest();

  const uploadApplicationAction = useAsyncCallback(
    async (_, baseUrl?: string) => {
      if (baseUrl) {
        return {
          deploymentUrl: baseUrl,
        };
      }

      const buildFiles = await loadBuildFiles.execute();
      const { deploymentBaseUrl, deploymentId } =
        await deployFiles.execute(buildFiles);

      return {
        deploymentUrl: deploymentBaseUrl,
        deploymentId,
      };
    }
  );

  const createPreviewTagAction = useAsyncCallback(
    async (_, serverUrl: string, deploymentId?: string) => {
      const gitCommitHash = (await isInsideGitRepo(model.projectFolder))
        ? await getGitLatestCommitHash(model.projectFolder)
        : undefined;

      const manifest = await readAppManifest({
        projectFolder: model.projectFolder,
        serverUrl,
      });

      const updatedManifest = deploymentId
        ? updateManifestWithBackendWorker(
            manifest,
            deploymentId,
            serverUrl,
            label,
            gitCommitHash
          )
        : manifest;

      return devCenterOverrides.createLooseAppRelease(
        updatedManifest,
        model.releaseAppId
      );
    }
  );

  const { status: createPreviewStatus } = useAsync(async () => {
    await assertBuildOutputExists(model.projectFolder);

    const { deploymentUrl: previewUrl, deploymentId } =
      await uploadApplicationAction.execute(baseUrl);

    const previewId = await createPreviewTagAction.execute(
      previewUrl,
      deploymentId
    );

    return { previewId, previewUrl };
  }, []);

  return (
    <>
      <ActionStatus
        status={uploadApplicationAction.status}
        loadingText={'Uploading your application files...'}
        successText={'Uploaded successfully!'}
      />
      <ActionStatus
        status={createPreviewTagAction.status}
        loadingText={'Creating a preview...'}
        successText={'Preview created successfully!'}
      />
      {match(createPreviewStatus, {
        Error: () => null,
        Loading: () => null,
        Success: ({ result: { previewId, previewUrl } }) =>
          model.config.projectType === 'Site' ? (
            <SiteTopologyAndLinks
              environment={environment}
              previewUrl={previewUrl}
              previewId={previewId}
              onTopologyUpdated={() =>
                onPreviewCreated?.({ previewId, previewUrl })
              }
            />
          ) : (
            <AppTopologyAndLinks
              environment={environment}
              previewId={previewId}
              onTopologyUpdated={() => onPreviewCreated?.({ previewId })}
            />
          ),
      })}
    </>
  );
};
